
import { API_URL } from "../../constants";
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";
import authSession from "../../lib/authSession";

/**
 * How long a fetched dashboard snapshot stays "fresh". Inside this window a
 * revisit renders the cache with no network call; once it lapses the page still
 * renders instantly from cache and revalidates in the background, so new data
 * always lands. `{ force: true }` (Refresh button) revalidates immediately.
 */
export const DASHBOARD_CACHE_TTL_MS = 60 * 1000;

export const dashboardInitialState = {
  dashboard: {},
  dashboardLoading: false,
  dashboardError: null,
  // When the current snapshot was fetched (ms since epoch) — drives the cache.
  dashboardFetchedAt: null,
  refreshLoading: false,
  refreshError: null,
};

export function createDashboardActions(set, get) {
  /** True when a cached snapshot exists and is still within its TTL. */
  const isCacheFresh = (ttlMs) => {
    const { dashboard, dashboardFetchedAt } = get();
    return Boolean(
      dashboardFetchedAt &&
        dashboard &&
        Object.keys(dashboard).length > 0 &&
        Date.now() - dashboardFetchedAt < ttlMs
    );
  };

  return {
    fetchDashboard: async ({ force = false, ttlMs = DASHBOARD_CACHE_TTL_MS } = {}) => {
      // Cached and still fresh → serve it without another round-trip.
      if (!force && isCacheFresh(ttlMs)) return { success: true, cached: true };
      // A request is already in flight → don't fire a duplicate.
      if (get().dashboardLoading) return { success: true, cached: true };

      // With a cached snapshot the revalidation is silent (no spinner): the page
      // keeps rendering the last data and swaps in fresh numbers when they land.
      const hasCache = Object.keys(get().dashboard || {}).length > 0;
      set({ dashboardLoading: !hasCache, dashboardError: null });
      try {
        const response = await fetch(`${API_URL}/api/admin/dashboard`, {
          headers: authHeaders(resolveToken(get)),
        });
        if (!response.ok) throw new Error("Failed to fetch dashboard");
        const result = await response.json();
        // The live endpoint returns `{ statistics, recentActivity }`. Normalize
        // into a flat dashboard object the page can render directly.
        const statistics = result.statistics || result.data?.statistics || result.data || result;
        const recentActivity = result.recentActivity || result.data?.recentActivity || null;
        set({
          dashboardLoading: false,
          dashboard: {
            ...statistics,
            recentActivity,
          },
          dashboardFetchedAt: Date.now(),
          dashboardError: null,
        });
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch dashboard");
        set({ dashboardLoading: false, dashboardError: msg });
        return { success: false, error: msg };
      }
    },

    /**
     * Mark the cached snapshot stale so the next visit refetches. Triggered
     * automatically whenever an admin mutation succeeds (see adminStore).
     */
    invalidateDashboard: () => {
      set({ dashboardFetchedAt: null });
      return { success: true };
    },

    /**
     * Refresh the admin access token via the httpOnly refresh cookie.
     * Skipped while the in-memory token is still valid, so revisiting the
     * dashboard doesn't fire an extra request (`{ force: true }` bypasses).
     * (A 401 on any API call also triggers a transparent refresh + retry — see
     * `lib/authSession.js`.)
     */
    refreshSession: async ({ force = false } = {}) => {
      if (!force && !authSession.isTokenExpired(authSession.getToken())) {
        return { success: true, cached: true };
      }

      set({ refreshLoading: true, refreshError: null });

      try {
        await authSession.refresh("admin");
        set({
          refreshLoading: false,
          refreshError: null,
          token: authSession.getToken(),
          isAuthenticated: true,
        });
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to refresh session");
        set({ refreshLoading: false, refreshError: msg });
        return { success: false, error: msg };
      }
    },
  };
}

const dashboardActions = createDashboardActions;
export default dashboardActions;
